/**
 * Webhook Selectors
 * Selectors for querying webhook state
 */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { WebhookState } from './webhook.reducer';
import { calculateSuccessRate } from '../models/webhook.models';

/**
 * Feature Selector
 */
export const selectWebhookState = createFeatureSelector<WebhookState>('webhooks');

/**
 * Basic Selectors
 */
export const selectWebhooks = createSelector(
  selectWebhookState,
  (state) => state.webhooks
);

export const selectSelectedWebhook = createSelector(
  selectWebhookState,
  (state) => state.selectedWebhook
);

export const selectDeliveries = createSelector(
  selectWebhookState,
  (state) => state.deliveries
);

export const selectCreatedWebhookSecret = createSelector(
  selectWebhookState,
  (state) => state.createdWebhookSecret
);

export const selectTestResult = createSelector(
  selectWebhookState,
  (state) => state.testResult
);

/**
 * Pagination Selectors
 */
export const selectTotalWebhooks = createSelector(
  selectWebhookState,
  (state) => state.totalWebhooks
);

export const selectCurrentPage = createSelector(
  selectWebhookState,
  (state) => state.currentPage
);

export const selectPageSize = createSelector(
  selectWebhookState,
  (state) => state.pageSize
);

export const selectTotalDeliveries = createSelector(
  selectWebhookState,
  (state) => state.totalDeliveries
);

export const selectDeliveriesPage = createSelector(
  selectWebhookState,
  (state) => state.deliveriesPage
);

export const selectDeliveriesPageSize = createSelector(
  selectWebhookState,
  (state) => state.deliveriesPageSize
);

/**
 * Loading State Selectors
 */
export const selectLoading = createSelector(
  selectWebhookState,
  (state) => state.loading
);

export const selectCreating = createSelector(
  selectWebhookState,
  (state) => state.creating
);

export const selectUpdating = createSelector(
  selectWebhookState,
  (state) => state.updating
);

export const selectDeleting = createSelector(
  selectWebhookState,
  (state) => state.deleting
);

export const selectTesting = createSelector(
  selectWebhookState,
  (state) => state.testing
);

export const selectLoadingDeliveries = createSelector(
  selectWebhookState,
  (state) => state.loadingDeliveries
);

export const selectError = createSelector(
  selectWebhookState,
  (state) => state.error
);

/**
 * Computed Selectors
 */
export const selectWebhookCount = createSelector(
  selectWebhooks,
  (webhooks) => webhooks.length
);

export const selectActiveWebhooks = createSelector(
  selectWebhooks,
  (webhooks) => webhooks.filter(w => w.is_active)
);

export const selectInactiveWebhooks = createSelector(
  selectWebhooks,
  (webhooks) => webhooks.filter(w => !w.is_active)
);

export const selectActiveWebhookCount = createSelector(
  selectActiveWebhooks,
  (webhooks) => webhooks.length
);

export const selectHealthyWebhooks = createSelector(
  selectWebhooks,
  (webhooks) => webhooks.filter(w => {
    const successRate = calculateSuccessRate(w.statistics);
    return successRate >= 90;
  })
);

export const selectUnhealthyWebhooks = createSelector(
  selectWebhooks,
  (webhooks) => webhooks.filter(w => {
    const successRate = calculateSuccessRate(w.statistics);
    return successRate < 90 && w.statistics.total_deliveries > 0;
  })
);

export const selectHasWebhooks = createSelector(
  selectWebhookCount,
  (count) => count > 0
);

export const selectHasSecret = createSelector(
  selectCreatedWebhookSecret,
  (secret) => secret !== null
);

